/**
 * Constants for subscription-related components
 * Centralizes all hardcoded strings for consistency and easier maintenance
 */

/**
 * Data metrics identifiers for analytics tracking
 */
export const SUBSCRIPTION_METRICS = {
    // Sign up form metrics
    SIGN_UP_FIRST_NAME: 'cloudngfw-sign-up-first-name',
    SIGN_UP_LAST_NAME: 'cloudngfw-sign-up-last-name',
    SIGN_UP_EMAIL: 'cloudngfw-sign-up-email',
    SIGN_UP_HAS_BILLING_CODE: 'cloudngfw-sign-up-has-billing-association-code',
    SIGN_UP_BILLING_CODE: 'cloudngfw-scm-sign-up-billing-code',
    SIGN_UP_SAVE: 'cloudngfw-sign-up-save',
    SIGN_UP_LINK_NEW_TENANT: 'cloudngfw-sign-up-link-new-tenant-button',
    SIGN_UP_SCM_CODE_ASSOCIATED: 'cloudngfw-sign-up-scm-code-associated',

    // Link account metrics
    LINK_ACCOUNT_ACCOUNT_ID: 'cloudngfw-link-account-accountId',
    LINK_ACCOUNT_EMAIL: 'cloudngfw-link-account-email',
    LINK_ACCOUNT_TENANT: 'cloudngfw-link-account-tenant',
    LINK_ACCOUNT_ASSOCIATION_CODE: 'cloudngfw-link-account-association-code',
    LINK_ACCOUNT_CONTINUE: 'cloudngfw-link-account-continue',
    LINK_TENANT_LOGIN: 'cloudngfw-link-tenant-login',
    EXPIRED_SESSION_LOGIN: 'cloudngfw-expired-session-login',

    // Account linked metrics
    ACCOUNT_LINKED_CONTINUE: 'cloudngfw-account-linked-continue',

    // Other metrics
    GET_LOGIN_HELP: 'cloudngfw-get-login-help',
} as const;

/**
 * Form field names used across subscription components
 */
export const SUBSCRIPTION_FIELDS = {
    ACCOUNT_ID: 'AccountId',
    USER_NAME: 'UserName',
    FIRST_NAME: 'FirstName',
    LAST_NAME: 'LastName',
    BILLING_ASSOCIATION_CODE: 'BillingAssociationCode',
    HAS_BILLING_ASSOCIATION_CODE: 'HasBillingAssociationCode',
    TENANT_ID: 'TenantId',
} as const;

/**
 * Translation keys for subscription forms
 */
export const SUBSCRIPTION_I18N_KEYS = {
    // Generic
    AWS_ACCOUNT_ID: 'generic.awsAccountId',
    TENANT_ID: 'generic.tenantId',
    CONTINUE: 'generic.continue',
    SAVE: 'generic.save',
    LOGIN: 'generic.login',
    LOADING: 'generic.loading',
    ERROR: 'generic.error',
    YES: 'generic.yes',
    NO: 'generic.no',
    REQUEST_FAILED: 'generic.requestFailed',

    // Auth
    TITLE: 'auth.title',
    EMAIL: 'auth.email',
    NAME: 'auth.name',
    LAST_NAME: 'auth.lastName',
    HAVE_BILLING_CODE: 'auth.haveBillingAssociationCode',
    BILLING_CODE: 'auth.billingAssociationCode',
    BILLING_SUBSCRIPTION_CODE: 'auth.billingSubscriptionCode',
    BILLING_CODE_TOOLTIP: 'auth.billingAssociationCodeTooltip',

    // Activation
    ACTIVATE_MESSAGE: 'activate.message',
    ACTIVATE_GET_HELP: 'activate.getHelp',
    ACTIVATE_LOGIN: 'activate.login',

    // Account linked
    ACCOUNT_LINKED_SUBTITLE: 'acctLinked.subTitle',
    ACCOUNT_LINKED_DESCRIPTION: 'acctLinked.description',

    // SCM billing code association
    SCM_BILLING_SUBTITLE: 'scmBillingCodeAssociation.subTitle',
    SCM_BILLING_DESCRIPTION: 'scmBillingCodeAssociation.description',
    SCM_BILLING_SUBMIT: 'scmBillingCodeAssociation.submitButton',

    // Link tenant successful
    LINK_TENANT_SUBTITLE: 'linkTenantSuccessful.subTitle',
    LINK_TENANT_DESCRIPTION: 'linkTenantSuccessful.description',

    // Link account
    LINK_ACCOUNT_IN_PROGRESS: 'linkAccount.inProgress',

    // No tenant
    NO_TENANT_SUBTITLE: 'noTenant.subTitle',
    NO_TENANT_DESCRIPTION: 'noTenant.description',
    NO_TENANT_EXPIRED_SESSION: 'noTenant.expiredSession',

    // Help content - Cloud NGFW PAYG
    CNGFW_PAYG_TITLE: 'subscriptionHelpContent.cngfwPayg.title',
    CNGFW_PAYG_SUBTITLE: 'subscriptionHelpContent.cngfwPayg.subtitle',
    CNGFW_PAYG_HEADER: 'subscriptionHelpContent.cngfwPayg.header',
    CNGFW_PAYG_DESCRIPTION_1: 'subscriptionHelpContent.cngfwPayg.description1',
    CNGFW_PAYG_DESCRIPTION_2: 'subscriptionHelpContent.cngfwPayg.description2',
    CNGFW_PAYG_LINK_BILLING_TITLE: 'subscriptionHelpContent.cngfwPayg.linkBillingTitle',
    CNGFW_PAYG_LINK_BILLING_DESC: 'subscriptionHelpContent.cngfwPayg.linkBillingDesc',
    CNGFW_PAYG_PROTECTION_TITLE: 'subscriptionHelpContent.cngfwPayg.protectionTitle',
    CNGFW_PAYG_PROTECTION_DESC: 'subscriptionHelpContent.cngfwPayg.protectionDesc',
    CNGFW_PAYG_SUPPORT_TEXT: 'subscriptionHelpContent.cngfwPayg.supportText',

    // Help content - Cloud NGFW Trial
    CNGFW_TRIAL_TITLE: 'subscriptionHelpContent.cngfwTrial.title',
    CNGFW_TRIAL_SUBTITLE: 'subscriptionHelpContent.cngfwTrial.subtitle',
    CNGFW_TRIAL_DESCRIPTION: 'subscriptionHelpContent.cngfwTrial.description',
    CNGFW_TRIAL_SIMPLE_SETUP_TITLE: 'subscriptionHelpContent.cngfwTrial.simpleSetupTitle',
    CNGFW_TRIAL_SIMPLE_SETUP_DESC: 'subscriptionHelpContent.cngfwTrial.simpleSetupDesc',
    CNGFW_TRIAL_CUSTOMIZE_TITLE: 'subscriptionHelpContent.cngfwTrial.customizeTitle',
    CNGFW_TRIAL_CUSTOMIZE_DESC: 'subscriptionHelpContent.cngfwTrial.customizeDesc',
    CNGFW_TRIAL_SECURE_TITLE: 'subscriptionHelpContent.cngfwTrial.secureTitle',
    CNGFW_TRIAL_SECURE_DESC: 'subscriptionHelpContent.cngfwTrial.secureDesc',
    CNGFW_TRIAL_FEATURES_INTRO: 'subscriptionHelpContent.cngfwTrial.featuresIntro',
    CNGFW_TRIAL_FEATURE_1: 'subscriptionHelpContent.cngfwTrial.feature1',
    CNGFW_TRIAL_FEATURE_2: 'subscriptionHelpContent.cngfwTrial.feature2',
    CNGFW_TRIAL_FEATURE_3: 'subscriptionHelpContent.cngfwTrial.feature3',
    CNGFW_TRIAL_SUPPORT_TEXT: 'subscriptionHelpContent.cngfwTrial.supportText',

    // Help content - AIRFM PAYG
    AIRFM_PAYG_TITLE: 'subscriptionHelpContent.airfmPayg.title',
    AIRFM_PAYG_SUBTITLE: 'subscriptionHelpContent.airfmPayg.subtitle',
    AIRFM_PAYG_HEADER: 'subscriptionHelpContent.airfmPayg.header',
    AIRFM_PAYG_DESCRIPTION_1: 'subscriptionHelpContent.airfmPayg.description1',
    AIRFM_PAYG_DESCRIPTION_2: 'subscriptionHelpContent.airfmPayg.description2',
    AIRFM_PAYG_LINK_BILLING_TITLE: 'subscriptionHelpContent.airfmPayg.linkBillingTitle',
    AIRFM_PAYG_LINK_BILLING_DESC: 'subscriptionHelpContent.airfmPayg.linkBillingDesc',
    AIRFM_PAYG_PROTECTION_TITLE: 'subscriptionHelpContent.airfmPayg.protectionTitle',
    AIRFM_PAYG_PROTECTION_DESC: 'subscriptionHelpContent.airfmPayg.protectionDesc',
    AIRFM_PAYG_SUPPORT_TEXT: 'subscriptionHelpContent.airfmPayg.supportText',

    // Help content - AIRFM Trial
    AIRFM_TRIAL_TITLE: 'subscriptionHelpContent.airfmTrial.title',
    AIRFM_TRIAL_SUBTITLE: 'subscriptionHelpContent.airfmTrial.subtitle',
    AIRFM_TRIAL_DESCRIPTION: 'subscriptionHelpContent.airfmTrial.description',
    AIRFM_TRIAL_SIMPLE_SETUP_TITLE: 'subscriptionHelpContent.airfmTrial.simpleSetupTitle',
    AIRFM_TRIAL_SIMPLE_SETUP_DESC: 'subscriptionHelpContent.airfmTrial.simpleSetupDesc',
    AIRFM_TRIAL_AI_TITLE: 'subscriptionHelpContent.airfmTrial.aiTitle',
    AIRFM_TRIAL_AI_DESC: 'subscriptionHelpContent.airfmTrial.aiDesc',
    AIRFM_TRIAL_SECURE_TITLE: 'subscriptionHelpContent.airfmTrial.secureTitle',
    AIRFM_TRIAL_SECURE_DESC: 'subscriptionHelpContent.airfmTrial.secureDesc',
    AIRFM_TRIAL_FEATURES_INTRO: 'subscriptionHelpContent.airfmTrial.featuresIntro',
    AIRFM_TRIAL_FEATURE_1: 'subscriptionHelpContent.airfmTrial.feature1',
    AIRFM_TRIAL_FEATURE_2: 'subscriptionHelpContent.airfmTrial.feature2',
    AIRFM_TRIAL_FEATURE_3: 'subscriptionHelpContent.airfmTrial.feature3',
    AIRFM_TRIAL_SUPPORT_TEXT: 'subscriptionHelpContent.airfmTrial.supportText',

    // Help content - AIRFM unified
    AIRFM_WELCOME_TITLE: 'subscriptionHelpContent.airfm.welcomeTitle',
    AIRFM_GET_STARTED_TITLE: 'subscriptionHelpContent.airfm.getStartedTitle',
    AIRFM_NEW_USER_TITLE: 'subscriptionHelpContent.airfm.newUserTitle',
    AIRFM_NEW_USER_DESC: 'subscriptionHelpContent.airfm.newUserDesc',
    AIRFM_EXISTING_USER_TITLE: 'subscriptionHelpContent.airfm.existingUserTitle',
    AIRFM_EXISTING_USER_DESC: 'subscriptionHelpContent.airfm.existingUserDesc',
    AIRFM_WHY_CHOOSE_TITLE: 'subscriptionHelpContent.airfm.whyChooseTitle',
    AIRFM_THREAT_PROTECTION_TITLE: 'subscriptionHelpContent.airfm.threatProtectionTitle',
    AIRFM_THREAT_PROTECTION_DESC: 'subscriptionHelpContent.airfm.threatProtectionDesc',
    AIRFM_SCALING_TITLE: 'subscriptionHelpContent.airfm.scalingTitle',
    AIRFM_SCALING_DESC: 'subscriptionHelpContent.airfm.scalingDesc',
    AIRFM_COMPLIANCE_TITLE: 'subscriptionHelpContent.airfm.complianceTitle',
    AIRFM_COMPLIANCE_DESC: 'subscriptionHelpContent.airfm.complianceDesc',
    AIRFM_SUPPORT_TITLE: 'subscriptionHelpContent.airfm.supportTitle',
    AIRFM_SUPPORT_TEXT: 'subscriptionHelpContent.airfm.supportText',

    // Help content - Common
    HELP_SUPPORT_EMAIL: 'subscriptionHelpContent.common.supportEmail',
    HELP_SUPPORT_LINK: 'subscriptionHelpContent.common.supportLink',
} as const;

/**
 * Feature flags for subscription flows
 * These are client-side flags since the subscription flow is pre-login (no tenant context)
 */
export const SUBSCRIPTION_FEATURE_FLAGS = {
    /** Controls AIRFM workflow for already-linked accounts (link-state 'linked' or 'in_metering_group').
     *  When disabled: AIRFM uses the CNGFW workflow for linked accounts.
     *  When enabled: AIRFM uses its own workflow even for linked accounts.
     *  For non-linked accounts, AIRFM always uses its own workflow regardless of this flag. */
    AIRFM_TENANT_CREATION: process.env.REACT_APP_ENABLE_AIRFM_TENANT_CREATION === 'true',
} as const;

/**
 * Toast notification IDs to prevent duplicates
 */
export const SUBSCRIPTION_TOAST_IDS = {
    CREATE_FIREWALL: 'create-firewall',
    VALIDATE_SCM_CODE: 'validate-scm-code',
    ASSOCIATE_SCM_CODE: 'associate-scm-code',
    VALIDATE_ASSOCIATE_SCM_CODE: 'validate-associate-scm-code',
    CREATE_SUBSCRIPTION_LINK_ACCOUNT: 'crete-subscription-link-account',
    SUBSCRIPTION_LINK_ACCOUNT_TOKEN: 'subscription-link-account-token',
    LIST_SUBSCRIPTIONS: 'list-subscriptions',
    CREATE_TENANT: 'create-tenant',
    CLOUD_MANAGER: 'cloud-manager',
} as const;

/**
 * External URLs
 */
export const SUBSCRIPTION_URLS = {
    LOGIN_ASSISTANCE: 'https://www.paloaltonetworks.com/services/support/login-assistance',
    SUPPORT: 'https://support.paloaltonetworks.com/Support/Index',
    SUPPORT_EMAIL: 'support@paloaltonetworks.com',
} as const;

/**
 * Radio group options for "Has Billing Association Code" field
 */
export const BILLING_CODE_OPTIONS = [
    {
        value: 'Yes',
        labelKey: SUBSCRIPTION_I18N_KEYS.YES,
    },
    {
        value: 'No',
        labelKey: SUBSCRIPTION_I18N_KEYS.NO,
    },
] as const;
